import React, { useEffect, useState } from 'react';
import { ScheduleService } from '../../service/ScheduleService';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import KitModal from '../UI/KitModal';
import useFlowsStore from '../../store/storeFlows';
import { generateCustomID } from '../../util/generateCustomId';
import { useSocketStore } from '../../store/storeSocket';
import { useNotificationStore } from '../../store/storeNotifications';
import { useAuthStore } from '../../store/storeAuth';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { MultiDatePicker } from '../MultiDatePicker/MultiDatePicker';

dayjs.extend(utc);
dayjs.extend(timezone);

export const intervalTypes = ['hourly', 'daily', 'weekly', 'monthly'];
export const intervalWeekdays = [
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
  { label: "Sunday", value: 7 },
];

interface HourlyInputProps {
  intervalData: any;
  onChange: (e: { target: { value: string; name: string } }) => void;
  user: any;
}

export const HourlyInput: React.FC<HourlyInputProps> = ({
  intervalData,
  onChange,
  user,
}) => {
  const handleChange = (time: dayjs.Dayjs | null) => {
    if (!time) return;

    const minutes = time?.minute();
    const seconds = time?.second();

    onChange({
      target: {
        value: seconds.toString(),
        name: 'minutely',
      },
    });

    onChange({
      target: {
        value: minutes.toString(),
        name: 'hourly',
      },
    });
  };

  const defaultMinute = parseInt(intervalData?.hourly || '0', 10);
  const defaultSecond = parseInt(intervalData?.minutely || '0', 10);
  const defaultValue = dayjs().minute(defaultMinute).second(defaultSecond);

  return (
    <FormControl sx={{ width: '200px' }}>
      <TimePicker
        timezone="America/New_York"
        views={['minutes', 'seconds']}
        ampm={false}
        ampmInClock={false}
        format="mm:ss" label={`Select Time, timezone: "EST"`}
        defaultValue={defaultValue}
        onChange={handleChange}
      />
    </FormControl>
  );
};

interface DailyInputProps {
  intervalData: any;
  onChange: (e: { target: { value: string; name: string } }) => void;
}

export const DailyInput: React.FC<DailyInputProps> = ({
  intervalData,
  onChange,
}) => {
  const handleChange = (time: dayjs.Dayjs | null) => {
    if (!time) return;

    const hours = time?.hour();
    const minutes = time?.minute();
    const seconds = time?.second();

    onChange({
      target: {
        value: hours.toString(),
        name: 'daily',
      },
    });

    onChange({
      target: {
        value: minutes.toString(),
        name: 'hourly',
      },
    });

    onChange({
      target: {
        value: seconds.toString(),
        name: 'minutely',
      },
    });
  };

  const defaultHour = parseInt(intervalData?.daily || '0', 10);
  const defaultMinute = parseInt(intervalData?.hourly || '0', 10);
  const defaultSecond = parseInt(intervalData?.minutely || '0', 10);
  const defaultValue = dayjs().hour(defaultHour).minute(defaultMinute).second(defaultSecond);

  return (
    <TimePicker
      label={`Select Time, timezone: "EST"`}
      defaultValue={defaultValue}
      onChange={handleChange}
      slotProps={{ textField: { fullWidth: false } }}
      views={['hours', 'minutes', 'seconds']}
      format="HH:mm:ss"
      ampm={false}
      ampmInClock={false}
    />
  );
};

interface MonthlyInputProps {
  intervalData: any;
  onChange: (e: { target: { value: string; name: string } }) => void;
  user: any;
}

export const MonthlyInput: React.FC<MonthlyInputProps> = ({
  intervalData,
  onChange,
}) => {
  const [value, setValue] = useState<number[]>(() => {
    if (intervalData.monthly === '*') return [1];
    
    return intervalData.monthly.split(',');
  });

  const handleDateChange = (date: number[]) => {
    setValue(date)

    onChange({
      target: {
        value: date.join(','),
        name: 'monthly',
      },
    });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <MultiDatePicker value={value} onChange={handleDateChange} />
    </div>
  );
};

interface WeeklyInputProps {
  user: any;
  intervalData: any;
  onChange: (e: { target: { value: string; name: string } }) => void;
}

const WeeklyInput = ({
  user,
  intervalData,
  onChange,
}: WeeklyInputProps) => {
  const [value, setValue] = useState<string[]>(() => {
    if (intervalData.weekly === '*') return [intervalWeekdays[0].value]
    
    return intervalData.weekly.split(',');
  });

  const handleOnWeekSelectChange = (value: SelectChangeEvent<string[]>) => {
    if (!Array.isArray(value.target.value)) return;

    const selectedDaysFiltered = value.target.value.filter(day => day !== '-');

    if (selectedDaysFiltered.length === 0) {
      setValue(['-']);

      return;
    }

    setValue(selectedDaysFiltered);

    onChange({
      target: {
        value: selectedDaysFiltered.join(','),
        name: 'weekly',
      }
    })
  }

  return (
    <>
      <label
        style={{
          color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
        }}
      >
        Every&nbsp;
      </label>
      <FormControl sx={{ width: '200px' }}>
        <InputLabel>Select Days of the Week</InputLabel>
        <Select
          label="Select Days of the Week"
          multiple
          value={value}
          onChange={handleOnWeekSelectChange}
          name="weekly"
          sx={{
            backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            color: user?.theme === 'dark' ? '#98A2B3' : 'black',
          }}
        >
          <MenuItem value="-" disabled>Choose</MenuItem>
          {intervalWeekdays.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <label
        style={{
          color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
        }}
      >
        &nbsp;at:&nbsp;
      </label>
      <DailyInput intervalData={intervalData} onChange={onChange} />
    </>
  );
}

const AutoTriggerTimerModal = ({
  isAutoTriggerModalOpen,
  setIsAutoTriggerModalOpen,
  formData,
  setFormData,
  intervalData,
  setIntervalData,
  saveNode,
}: any) => {
  const { flowId } = useFlowsStore((state) => state);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { user } = useAuthStore((state) => state);
  const { setProcessingFlows, setDeleteProcessingFlow } = useSocketStore(
    (state) => state
  );
  const [schedule, setSchedule] = useState<any>({});

  useEffect(() => {
    if (!(formData.isAutotrigger && flowId && isAutoTriggerModalOpen)) return;
  
    const getSchedule = async () => {
      try {
        const res: any = await ScheduleService.getSchedules();
        
        const flowSchedule = res?.data?.find(
          (schedule: { flowId: string }) => schedule.flowId === flowId
        );

        if (flowSchedule?.flowId === flowId && formData.isAutotrigger) {
          setProcessingFlows(flowId);
        }

        setSchedule(flowSchedule);
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: error?.message,
        });
      }
    };
  
    getSchedule();
  }, [flowId, formData.isAutotrigger, isAutoTriggerModalOpen, setNotification, setProcessingFlows]);

  const handleOnTypeChange = ({ target: { value } }: { target: { value: string; name: string } }) => {
    setIntervalData({
      hourly: '*',
      daily: '*',
      weekly: '*',
      monthly: '*',
      codedTime: '',
      inputText: '',
      varInput: '',
      type: value,
    });
  }

  const onChange = (e: { target: { value: string; name: string } }) => {
    const { name, value } = e.target;

    setIntervalData((prev: { minutely: string; hourly: string; daily: string; monthly: string; weekly: string }) => {    
      const newIntervalData = {
        ...prev,
        [name]: value,
      };

      return {
        ...newIntervalData,
        codedTime: `${newIntervalData.minutely} ${newIntervalData.hourly} ${newIntervalData.daily} ${newIntervalData.monthly} * ${newIntervalData.weekly}`,
      }
    });
  };
  
    const getTriggerMessage = (type: string) => {
    const seconds = intervalData?.minutely?.toString().padStart(2, '0') || '00';
    const minutes = intervalData?.hourly?.toString().padStart(2, '0') || '00';
    const hour = intervalData?.daily?.toString().padStart(2, '0') || '00';
  
    switch (type) {
      case 'hourly':
        return `Runs every hour at ${minutes} minute(s) and ${seconds} second(s).`;
      case 'daily':
        return `Runs every day at ${hour}:${minutes}:${seconds}.`;
      case 'weekly': {
        const weekDays = intervalData?.weekly
          .split(',')
          .map((day: string) => {
            const weekday = intervalWeekdays.find(
              (wd) => wd.value.toString() === day.trim()
            );
            return weekday ? weekday.label : day;
          })
          .join(', ');
        return `Runs every week on ${weekDays} at ${hour}:${minutes}:${seconds}.`;
      }
      case 'monthly':
        return `Runs every month on day(s) ${intervalData?.monthly} at ${hour}:${minutes}:${seconds}.`;
      default:
        return '';
    }
  };

  const codedTime = (type: string, interval: string) => {
    const intervalArray = interval.split(' ');

    const intervalObj = {
      second: intervalArray?.[0],
      minute: intervalArray?.[1],
      hour: type === 'daily' || type === 'weekly' || type === 'monthly' ? intervalArray?.[2] : '*',
      day: type === 'monthly' ? intervalArray?.[3] : '*',
      weekDay: type === 'weekly' ? intervalArray?.[5] : '*',
    };

    return `${intervalObj.second} ${intervalObj.minute} ${intervalObj.hour} ${intervalObj.day} * ${intervalObj.weekDay}`;
  };

  const onSave = async () => {
    try {
      if (schedule?.id) {
        await ScheduleService.updateSchedule({
          name: schedule.name,
          id: schedule.id,
          interval: codedTime(intervalData.type, intervalData.codedTime),
        });
      } else {
        let objectData = {};
        if (intervalData.varInput) {
          objectData = {
            variableInputs: {
              inputId: intervalData.varInput,
              inputText: intervalData.inputText,
            },
          };
        }
        await ScheduleService.addSchedule({
          name: 'Flow Global Node' + generateCustomID(),
          interval: codedTime(intervalData.type, intervalData.codedTime),
          flowId: flowId,
          webhook: false,
          ...objectData,
        });
        setProcessingFlows(flowId);
      }

      saveNode({
        triggerMessage: getTriggerMessage(intervalData.type),
        intervalData: intervalData,
        isAutotrigger: true,
      });

      setFormData((prev: any) => ({
        ...prev,
        triggerMessage: getTriggerMessage(intervalData.type),
        intervalData: intervalData,
        isAutotrigger: true,
      }));
      setIsAutoTriggerModalOpen(false);
    } catch (error: any) {
      setNotification({
        type: 'error',
        message: error?.message,
      });
    }
  };

  const onDelete = async () => {
    try {
      setDeleteProcessingFlow(flowId);
      await ScheduleService.deleteSchedule(schedule?.id);
      setIsAutoTriggerModalOpen(false);
      setFormData((prev: any) => ({
        ...prev,
        isAutotrigger: false,
      }));
      saveNode({
        triggerMessage: '',
        intervalData: {},
        isAutotrigger: false,
      });
    } catch (error) {
      setIsAutoTriggerModalOpen(false);
      setFormData((prev: any) => ({
        ...prev,
        isAutotrigger: false,
      }));
    }
  };

  return (
    <div>
      <KitModal
        show={isAutoTriggerModalOpen}
        onHide={() => {
          setIsAutoTriggerModalOpen(false);
          setFormData((prev: any) => ({
            ...prev,
            isAutotrigger: schedule?.flowId === flowId,
          }));
        }}
        title="Set time"
        footer={
          <>
            <Button onClick={onSave} variant="contained" sx={{ mr: '15px' }}>
              {schedule?.id ? 'Update' : 'Save'}
            </Button>
            {schedule?.id && <Button onClick={onDelete}>Delete</Button>}
          </>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <FormControl sx={{ width: '200px' }}>
            <InputLabel>Select Type</InputLabel>
            <Select
              value={intervalData?.type || ''}
              onChange={handleOnTypeChange}
              label="Select Type"
              name="type"
              sx={{
                backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#98A2B3' : 'black',
              }}
            >
              {intervalTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {intervalData?.type === 'hourly' && (
            <HourlyInput intervalData={intervalData} onChange={onChange} user={user} />
          )}
          {intervalData?.type === 'daily' && (
            <DailyInput intervalData={intervalData} onChange={onChange} />
          )}
          {intervalData?.type === 'weekly' && (
            <WeeklyInput user={user} intervalData={intervalData} onChange={onChange} />
          )}
          {intervalData?.type === 'monthly' && (
            <>
              <MonthlyInput intervalData={intervalData} onChange={onChange} />
              <label
                style={{
                  color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                }}
              >
                &nbsp;at:&nbsp;
              </label>
              <DailyInput intervalData={intervalData} onChange={onChange} />
            </>
          )}
        </div>
      </KitModal>
    </div>
  );
};

export default AutoTriggerTimerModal;